import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSlidePanel } from '../../hooks/useSlidePanel';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import customerService, {
  CustomerTransaction,
  SubscriptionCard,
} from '../../services/customerService';
import UpdateCardModal from './UpdateCardModal';
import { formatCurrency } from '../../utils/currency';
import { CustomerWallet } from '../../types/customer';

/**
 * The customer's points panel: balance, transaction history, current plan, and
 * the packs they can buy more with.
 *
 * Opens from the points chip in the booking-page header. Uses the shared
 * `useSlidePanel` timing so it feels like every other slide-in in the app
 * rather than becoming a fourth variant.
 *
 * SCOPED TO ONE ORGANIZATION. Points bought from one host are not spendable
 * with another, so a panel that pooled them would be actively misleading about
 * what the viewer can spend here.
 */

interface PointsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  wallet: CustomerWallet | null;
  /**
   * The host whose shops this panel links to. Without it the "get more" links
   * have nowhere to go and are left out entirely.
   */
  username?: string;
}


const PointsPanel: React.FC<PointsPanelProps> = ({
  isOpen,
  onClose,
  wallet,
  username,
}) => {
  const { t, i18n } = useTranslation();
  const { signOut, me, refresh } = useCustomerAuth();
  const { isVisible, shouldRender } = useSlidePanel(isOpen);

  /**
   * The soonest points to lapse, and how many. Read off the wallet rather than
   * recomputed here — the server replays the ledger to get it, and a second
   * implementation in the browser is a second answer to a question about
   * somebody's money.
   *
   * A date already past is dropped: the sweep has simply not caught up, those
   * points are already out of the balance, and "expired yesterday" under a
   * balance that no longer contains them reads as an error.
   */
  const nextExpiry = (() => {
    const summary = wallet?.pointsExpiry;
    if (!summary?.nextExpiryAt || summary.nextExpiryAmount <= 0) return null;
    const date = new Date(summary.nextExpiryAt);
    if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) return null;
    return { date, amount: summary.nextExpiryAmount };
  })();

  const [entries, setEntries] = useState<CustomerTransaction[]>([]);
  const [balance, setBalance] = useState(wallet?.balance ?? 0);
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  /**
   * Which of this host's shops actually have something on them. A link to an
   * empty shop is worse than no link — it reads as a dead end rather than an
   * offer.
   */
  const [shops, setShops] = useState<{ plans: number; points: number }>({
    plans: 0,
    points: 0,
  });
  /** The card the plan charges next. Null until loaded, or if Stripe has none. */
  const [card, setCard] = useState<SubscriptionCard | null>(null);
  const [updatingCard, setUpdatingCard] = useState(false);

  /**
   * EVERYTHING that happened to this wallet, not just its points.
   *
   * This read the point ledger alone, so a customer paying monthly for a plan
   * saw no record of ever having paid — the money left their card and the panel
   * they were looking at knew nothing about it.
   */
  const load = useCallback(async () => {
    if (!wallet) return;
    setLoading(true);
    try {
      const result = await customerService.getCustomerTransactions(wallet.organizationId);
      setEntries(result.entries);
      setBalance(result.balance);
    } catch {
      // Non-fatal: the balance from the header still shows.
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    if (isOpen && wallet) load();
  }, [isOpen, wallet, load]);

  // The card is only meaningful while there is a plan to charge it.
  useEffect(() => {
    const subscriptionId = wallet?.subscription?.id;
    if (!isOpen || !subscriptionId) {
      setCard(null);
      return;
    }
    let cancelled = false;
    customerService
      .getSubscriptionCard(subscriptionId)
      .then((result) => {
        if (!cancelled) setCard(result);
      })
      // Silent: the plan section is still useful without the card line.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isOpen, wallet?.subscription?.id, updatingCard]);

  useEffect(() => {
    if (!isOpen || !username) return;
    let cancelled = false;
    customerService
      .getShopSummary(username)
      .then((result) => {
        if (!cancelled) setShops(result);
      })
      // Silent: a link that could not load is a link that is not shown, which
      // is better than an error inside a panel about someone's balance.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isOpen, username]);

  if (!shouldRender) return null;

  // Widened to `string`: the unified history carries the reason as plain text,
  // and an unknown one falls through to itself rather than crashing the list.
  const reasonLabel = (reason: string): string =>
    ({
      purchase: t('booking:points.reason.purchase', 'Purchased'),
      booking: t('booking:points.reason.booking', 'Booking'),
      cancellation_refund: t('booking:points.reason.refund', 'Returned (cancelled)'),
      expiry: t('booking:points.reason.expiry', 'Expired'),
      admin: t('booking:points.reason.admin', 'Adjustment'),
    }[reason] ?? reason);

  /** What a card payment was for, when the product is not named on the row. */
  const paymentLabel = (purpose: string): string =>
    ({
      subscription: t('booking:points.paymentPlan', 'Plan payment'),
      point_pack: t('booking:points.paymentPoints', 'Points purchase'),
      booking: t('booking:points.paymentBooking', 'Booking payment'),
    }[purpose] ?? t('booking:points.paymentOther', 'Payment'));

  const subscription = wallet?.subscription ?? null;

  /**
   * Which systems this host actually has.
   *
   * Absent means "both" — that is what an older response meant before the
   * field existed, and defaulting to "neither" would empty the panel for
   * everyone the moment a cached response came back.
   *
   * A HELD subscription always keeps the plan section, even with the system
   * withdrawn. Somebody still being billed must be able to see and cancel what
   * they are paying for; hiding it would be the one thing worse than showing it.
   */
  const showPoints = wallet?.features?.points !== false;
  const showPlans = wallet?.features?.subscriptions !== false || !!subscription;

  const panel = (
    <div className="relative z-50">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={`fixed inset-0 bg-gray-500 transition-opacity duration-500 ${
            isVisible ? 'bg-opacity-75' : 'bg-opacity-0'
          }`}
          onClick={onClose}
        />

        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 lg:pl-10">
          <div
            className={`pointer-events-auto relative w-screen max-w-full lg:max-w-md transition transform duration-500 ease-out ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
          >
            <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
              {/* Header */}
              <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 truncate">{me?.email}</p>
                    {/* Only where points exist. A host without the point
                        system has no balance to headline, and "0 points" in
                        24px is a prominent way to say nothing. */}
                    {showPoints && (
                      <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {t('booking:points.balance', '{{count}} points', { count: balance })}
                      </p>
                    )}
                    {/* WHEN THEY GO. A balance that shrinks with no warning and
                        nothing on the statement to explain it is the one
                        complaint an expiring-points system reliably produces.
                        Absent whenever nothing lapses, which is every wallet
                        until a host sets a window on a pack. */}
                    {showPoints && nextExpiry && (
                      <p className="mt-1 text-xs font-medium text-amber-700">
                        {t(
                          'booking:points.expiringSoon',
                          '{{count}} points expire on {{date}}',
                          {
                            count: nextExpiry.amount,
                            date: nextExpiry.date.toLocaleDateString(i18n.language),
                          }
                        )}
                      </p>
                    )}
                    {wallet?.organizationName && (
                      <p className="text-xs text-gray-500 truncate">
                        {t('booking:points.withOrg', 'with {{org}}', {
                          org: wallet.organizationName,
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title={t('common:actions.close', 'Close')}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 space-y-6 px-5 py-5">
                {/* Current plan. Hidden entirely where the host has no plans:
                    "No active plan" is a hint of a feature that does not exist
                    here, which is exactly what the rollout is for. */}
                {showPlans && (
                <section>
                  <h3 className="mb-2 text-sm font-medium text-gray-900">
                    {t('booking:points.planTitle', 'Your plan')}
                  </h3>
                  {subscription ? (
                    <div className="rounded-md border border-gray-200 p-3">
                      <p className="text-sm font-medium text-gray-900">
                        {subscription.productTitle ??
                          t('booking:points.planFallback', 'Subscription')}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        {subscription.allowanceCount == null
                          ? t('booking:points.planUnlimited', 'Unlimited meetings')
                          : t(
                              'booking:points.planAllowance',
                              '{{count}} meetings per {{period}}',
                              {
                                count: subscription.allowanceCount,
                                period: t(
                                  `products:period.${subscription.allowancePeriod ?? 'month'}`,
                                  'month'
                                ),
                              }
                            )}
                      </p>
                      {subscription.currentPeriodEnd && (
                        <p className="mt-1 text-xs text-gray-500">
                          {subscription.cancelAtPeriodEnd
                            ? t('booking:points.planEnds', 'Ends {{date}}', {
                                date: new Date(
                                  subscription.currentPeriodEnd
                                ).toLocaleDateString(),
                              })
                            : t('booking:points.planRenews', 'Renews {{date}}', {
                                date: new Date(
                                  subscription.currentPeriodEnd
                                ).toLocaleDateString(),
                              })}
                        </p>
                      )}
                      {/* The card this plan will charge next, and a way to
                          change it. Someone whose card is about to expire had
                          no way to see which card was on file, let alone
                          replace it — their only options were to let the
                          payment fail or cancel. */}
                      <div className="mt-3 border-t border-gray-200 pt-3">
                        <p className="text-xs text-gray-500">
                          {t('booking:points.cardOnFile', 'Card to be charged')}
                        </p>
                        <div className="mt-1 flex items-center justify-between gap-3">
                          <p className="text-sm text-gray-900">
                            {card
                              ? `${card.brand ?? 'Card'} •••• ${card.last4 ?? '????'}`
                              : t('booking:points.noCard', 'No card on file')}
                          </p>
                          <button
                            type="button"
                            className="btn-secondary btn-sm flex-shrink-0"
                            onClick={() => setUpdatingCard(true)}
                          >
                            {t('booking:points.updateCard', 'Update card')}
                          </button>
                        </div>
                      </div>

                      {/* Cancelling here means "stop at the end of the period",
                          so until that date the plan is still live and changing
                          one's mind should cost nothing. Without resume, the
                          only way back was buying the plan again — a second
                          subscription for something they already had. */}
                      <button
                        type="button"
                        className={`mt-3 text-sm ${
                          subscription.cancelAtPeriodEnd ? 'btn-secondary btn-sm' : 'btn-text-danger'
                        }`}
                        disabled={canceling}
                        onClick={async () => {
                          const resuming = subscription.cancelAtPeriodEnd;
                          if (
                            !resuming &&
                            !window.confirm(
                              t(
                                'booking:points.cancelConfirm',
                                'Cancel this plan? It stays active until the end of the period you have already paid for.'
                              )
                            )
                          ) {
                            return;
                          }
                          setCanceling(true);
                          try {
                            if (resuming) {
                              await customerService.resumeSubscription(subscription.id);
                            } else {
                              await customerService.cancelSubscription(subscription.id);
                            }
                            await refresh();
                          } catch {
                            // Non-fatal: the plan is unchanged and they can retry.
                          } finally {
                            setCanceling(false);
                          }
                        }}
                      >
                        {canceling
                          ? t('common:status.processing', 'Processing...')
                          : subscription.cancelAtPeriodEnd
                            ? t('booking:points.resumePlan', 'Resume plan')
                            : t('booking:points.cancelPlan', 'Cancel plan')}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t('booking:points.noPlan', 'No active plan.')}
                    </p>
                  )}
                </section>
                )}

                {/* Get more — TO THE SHOP, not a checkout in here.

                    This used to list products and open a payment form inside the
                    panel. It could not complete a purchase, and it offered plans
                    the viewer was already on. The shop is where buying works,
                    has the description and images to decide by, and already
                    knows to leave out nothing. */}
                {username && (shops.points > 0 || shops.plans > 0) && (
                  <section>
                    <h3 className="mb-2 text-sm font-medium text-gray-900">
                      {t('booking:points.buyTitle', 'Get more')}
                    </h3>
                    <div className="space-y-2">
                      {shops.points > 0 && (
                        <a
                          href={`/${encodeURIComponent(username)}/shop/points`}
                          className="flex items-center justify-between rounded-md border border-gray-200 p-3 text-sm font-medium text-gray-900 hover:border-primary-400"
                        >
                          {t('booking:points.goToPointShop', 'Buy points')}
                          <span aria-hidden className="text-gray-400">
                            →
                          </span>
                        </a>
                      )}
                      {shops.plans > 0 && (
                        <a
                          href={`/${encodeURIComponent(username)}/shop/plans`}
                          className="flex items-center justify-between rounded-md border border-gray-200 p-3 text-sm font-medium text-gray-900 hover:border-primary-400"
                        >
                          {/* Wording depends on whether they are already on one:
                              "see the plans" reads oddly to someone shopping,
                              and "get a plan" reads oddly to someone who has. */}
                          {subscription
                            ? t('booking:points.seeOtherPlans', 'See other plans')
                            : t('booking:points.goToPlanShop', 'See plans')}
                          <span aria-hidden className="text-gray-400">
                            →
                          </span>
                        </a>
                      )}
                    </div>
                  </section>
                )}

                {/* History — points AND money, one list.

                    Two separate histories would make a customer check in two
                    places to answer one question, and the plan payments simply
                    were not shown anywhere at all. */}
                <section>
                  <h3 className="mb-2 text-sm font-medium text-gray-900">
                    {t('booking:points.historyTitleAll', 'History')}
                  </h3>
                  {loading && entries.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t('common:status.loading', 'Loading...')}
                    </p>
                  ) : entries.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t('booking:points.historyEmpty', 'Nothing here yet.')}
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                      {entries.map((entry) => (
                        <li
                          key={entry.id}
                          className="flex items-center justify-between px-3 py-2.5"
                        >
                          <span className="min-w-0">
                            <span className="block text-sm text-gray-900">
                              {entry.kind === 'payment'
                                ? entry.productName ?? paymentLabel(entry.purpose)
                                : reasonLabel(entry.reason)}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {new Date(entry.at).toLocaleString()}
                            </span>
                          </span>
                          {/* Money on the right in currency, points in points.
                              Showing a point count with a currency symbol is
                              the same mistake as pricing a plan in yen. */}
                          {entry.kind === 'payment' ? (
                            <span className="ml-3 flex-shrink-0 text-sm font-medium text-gray-900">
                              {formatCurrency(entry.amount, entry.currency)}
                            </span>
                          ) : (
                            <span
                              className={`ml-3 flex-shrink-0 text-sm font-medium ${
                                entry.delta >= 0 ? 'text-green-600' : 'text-gray-900'
                              }`}
                            >
                              {entry.delta >= 0 ? '+' : ''}
                              {entry.delta}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>

              <div className="border-t border-gray-200 px-5 py-4">
                <button
                  type="button"
                  className="btn-secondary w-full"
                  onClick={() => {
                    signOut();
                    onClose();
                  }}
                >
                  {t('booking:customer.signOut', 'Sign out')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {panel}
      {/* Rendered beside the panel rather than inside it: the panel is a
          scrolling column, and a payment form nested in one is where card
          fields end up half off-screen on a phone. */}
      {subscription && (
        <UpdateCardModal
          isOpen={updatingCard}
          onClose={() => setUpdatingCard(false)}
          subscriptionId={subscription.id}
          onUpdated={() => setUpdatingCard(false)}
        />
      )}
    </>,
    document.body
  );
};

export default PointsPanel;
